import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import Traec from "traec";

import { ErrorBoundary } from "storybook-dashboard/errors";
import { setNavBarItems } from "traec-react/navBar";

import CompanyInvites from "./companyInvite";
import Notifications from "../notifications";
import ProjectInvites from "./projectInvite";
import UserNotificationTree from "./tree";
import SummaryCardsRow from "./SummaryCardsRow";
import { styledTab, activeStyledTab } from "storybook-dashboard/utils";
import Octicon from "react-octicon";
import useApi from "storybook-dashboard/utils/fetching";
import { isSuperuser } from "storybook-dashboard/utils";
import { AddCompanyFormModal } from "AppSrc/user/tree";
import { useModal, Modal } from "storybook-dashboard/components/modal";

let navbarLinks = [{ label: " ", octicon: "home", to: "/" }];

function UserProfile(props) {
  let { user, dispatch, companyItems, projectItems, projects, companies } = props;
  const [activeTab, setActiveTab] = useState("notifications");
  const [notificationCounts, setNotificationCounts] = useState(0);
  const [dashboardsCount, setDashboardsCount] = useState(0);

  const { setModal } = useModal();
  const userFirstName = user?.get("first_name") || "";

  // Fetch data directly using useApi
  let { data: apiProjects } = useApi("/api/project/");
  let { data: apiCompanies, trigger: companyTrigger } = useApi("/api/company/");

  // Fetch invitation data using Traec (the original working approach)
  useEffect(() => {
    const requiredFetches = [
      new Traec.Fetch("company_invite_all", "list"),
      new Traec.Fetch("project_invite_all", "list"),
    ];

    Traec.fetchRequired(requiredFetches);
  }, []);

  // Calculate dashboards count from API data
  const actualDashboardsCount = dashboardsCount || (apiCompanies?.size || 0) + (apiProjects?.size || 0);

  // Calculate invitation counts from Redux data
  const totalInvitations = (companyItems?.size || 0) + (projectItems?.size || 0);

  const mainTabs = [
    {
      id: "notifications",
      name: "My pending actions",
      description:
        "Reports you have pending actions against. Click on the actions to submit, revise and review reports based on their current status.",
      number: notificationCounts,
      priority: notificationCounts > 0 ? "medium" : "normal",
    },
    {
      id: "dashboards",
      name: "My Dashboards",
      description: "Navigate between your dashboards and reports.",
      number: actualDashboardsCount,
      priority: "normal",
    },
    {
      id: "invitations",
      name: "Invitations",
      description: "Approve and reject outstanding company and project invitations.",
      number: totalInvitations,
      priority: "normal",
    },
    ...(isSuperuser(user)
      ? [
          {
            id: "admin",
            name: "Admins",
            description: "Administrative functions and company management tools.",
            number: 0,
            priority: "normal",
          },
        ]
      : []),
  ];

  const handleTabSwitch = (tabId) => {
    setActiveTab(tabId);
  };

  useEffect(() => {
    dispatch(setNavBarItems(navbarLinks));
  }, [dispatch]);

  return (
    <div className="h-100" style={{ overflowY: "auto" }}>
      <div className="container">
        <br />
        <div className="my-5 d-flex justify-content-between">
          <div>
            <h2 className="font-weight-bold ">Welcome to the Sustainability Tool {userFirstName}</h2>
            <p className="text-muted">
              Use this page to review outstanding invitations and navigate between your dashboards and reports.
            </p>
          </div>
        </div>
        <div className="">
          {/* Summary Cards Row (hidden for now) */}
          {/* 
          <SummaryCardsRow 
            notificationCounts={notificationCounts}
            totalInvitations={totalInvitations}
            actualDashboardsCount={actualDashboardsCount}
            companyItems={companyItems}
            projectItems={projectItems}
            apiCompanies={apiCompanies}
            apiProjects={apiProjects}
          />
          */}

          {/* Main Navigation Tabs */}
          <div className="d-flex mb-4" style={{ cursor: "pointer" }}>
            {mainTabs.map((tab) => {
              const getBadgeClass = () => {
                if (tab.priority === "high") return "badge-danger";
                if (tab.priority === "medium") return "badge-warning";
                return "badge-primary";
              };

              return (
                <a
                  key={tab.id}
                  className={`${styledTab} ${activeTab === tab.id ? `${activeStyledTab}` : ``}`}
                  onClick={() => handleTabSwitch(tab.id)}
                >
                  {tab.name}
                  {tab.number > 0 && <span className={`badge ${getBadgeClass()} ml-1`}>{tab.number}</span>}
                </a>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="shadow rounded p-4 mb-5">
            {activeTab === "notifications" && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h4 className="font-weight-bold mb-2">My pending actions</h4>
                    <p className="text-muted mb-0">{mainTabs.find((tab) => tab.id === "notifications")?.description}</p>
                  </div>
                  {notificationCounts > 0 && (
                    <div className="text-right">
                      <span className="badge badge-warning badge-lg">
                        <Octicon name="checklist" className="mr-1" />
                        {notificationCounts} Pending Actions
                      </span>
                    </div>
                  )}
                </div>

                {/* Quick Action Summary */}
                {notificationCounts > 0 && (
                  <div className="alert alert-light border-left-thick border-primary mb-4">
                    <div className="d-flex align-items-center">
                      <Octicon name="info" className="text-primary mr-3" style={{ fontSize: "20px" }} />
                      <div className="flex-grow-1">
                        <strong>Action Required:</strong> You have {notificationCounts} reports that need your
                        attention.
                        <div className="mt-2">
                          <small className="text-muted">
                            Use the filters below to focus on specific statuses or search for particular reports.
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <ErrorBoundary>
                  <Notifications setNotificationCounts={setNotificationCounts} />
                </ErrorBoundary>
              </>
            )}

            {activeTab === "dashboards" && (
              <>
                {/* <div className="float-right">
                  <span className="badge badge-outline-primary badge-lg">
                    <Octicon name="dashboard" className="mr-1" />
                    {actualDashboardsCount} Total Dashboards
                  </span>
                </div> */}

                <ErrorBoundary>
                  <UserNotificationTree user={user} setDashboardsCount={setDashboardsCount} />
                </ErrorBoundary>
              </>
            )}

            {activeTab === "invitations" && (
              <>
                <h4 className="font-weight-bold mb-3">Invitations</h4>
                <p className="text-muted mb-4">{mainTabs.find((tab) => tab.id === "invitations")?.description}</p>

                <ErrorBoundary>
                  {/* Invitations Section */}
                  <div className="row">
                    {/* Company Invitations */}
                    <div className="col-md-6 mb-4">
                      <div className="card h-100 shadow-sm">
                        <div className="card-header bg-primary text-white">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <Octicon name="organization" className="mr-2" />
                              <h5 className="mb-0 font-weight-bold">Company Invitations</h5>
                            </div>
                            <span className="badge badge-light badge-lg text-info font-weight-bold">
                              {companyItems?.size || 0}
                            </span>
                          </div>
                        </div>
                        <div className="card-body p-0">
                          <CompanyInvites />
                        </div>
                      </div>
                    </div>

                    {/* Project Invitations */}
                    <div className="col-md-6 mb-4">
                      <div className="card h-100 shadow-sm">
                        <div className="card-header bg-primary text-white">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <Octicon name="repo" className="mr-2" />
                              <h5 className="mb-0 font-weight-bold">Project Invitations</h5>
                            </div>
                            <span className="badge badge-light badge-lg text-info font-weight-bold">
                              {projectItems?.size || 0}
                            </span>
                          </div>
                        </div>
                        <div className="card-body p-0">
                          <ProjectInvites />
                        </div>
                      </div>
                    </div>
                  </div>
                </ErrorBoundary>
              </>
            )}

            {activeTab === "admin" && isSuperuser(user) && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h4 className="font-weight-bold mb-2">Administrative Tools</h4>
                    <p className="text-muted mb-0">{mainTabs.find((tab) => tab.id === "admin")?.description}</p>
                  </div>
                  <span className="badge badge-outline-secondary badge-lg">
                    <Octicon name="shield" className="mr-1" />
                    Admin Access
                  </span>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="card border-primary">
                      <div className="card-header bg-primary text-white">
                        <h5 className="mb-0">
                          <Octicon name="organization" className="mr-2" />
                          Company Management
                        </h5>
                      </div>
                      <div className="card-body">
                        <p className="card-text mb-3">
                          Click Create a Company to register a new company account. Projects and reports can then be
                          within the account.
                        </p>
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">Register new company accounts</small>
                          <button
                            className="btn btn-primary"
                            onClick={(e) => setModal(<AddCompanyFormModal trigger={companyTrigger} />)}
                          >
                            <Octicon name="plus" className="mr-1" />
                            Create a Company
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card border-secondary">
                      <div className="card-header bg-secondary text-white">
                        <h5 className="mb-0">
                          <Octicon name="gear" className="mr-2" />
                          System Settings
                        </h5>
                      </div>
                      <div className="card-body">
                        <p className="card-text mb-3">
                          Configure platform-wide sustainability settings such as metrics, indicators and conversion
                          factors.
                        </p>
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted">Platform configuration</small>
                          <Link to="/tenant/admin/" className="btn btn-secondary">
                            <Octicon name="tools" className="mr-1" />
                            Tenancy admin
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

UserProfile.propTypes = {
  isAuthenticated: PropTypes.bool,
};

const mapStateToProps = (state) => {
  // Raw data from entities
  const rawCompanyInvites = state.getInPath("entities.companyInvites.byId");
  const rawProjectInvites = state.getInPath("entities.projectInvites.byId");

  // Filtered data
  const companyItems = rawCompanyInvites?.filter((i) => i?.getInPath("company.name"));
  const projectItems = rawProjectInvites?.filter((i) => i?.getInPath("project_discipline.project.name"));

  return {
    user: state.getInPath(`auth.user`),
    userFirstName: state.getIn(["auth", "user", "first_name"]),
    companyItems,
    projectItems,
    // Add data for dashboard count
    projects: state.getInPath("entities.projects.byId"),
    companies: state.getInPath("entities.companies.byId"),
  };
};

export default connect(mapStateToProps)(UserProfile);
